import { Modal, ModalContent, ModalHeader, ModalBody } from '@heroui/react';
import { Package, Layers, Sofa, Armchair, Square, PanelLeft, Wrench, RectangleHorizontal, BrickWall, Bed } from 'lucide-react';

interface AddLineItemDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectCustom: () => void;
  onSelectFromLibrary: () => void;
  onSelectBanquette: () => void;
  onSelectBackCushion: () => void;
  onSelectSeatCushion: () => void;
  onSelectArm: () => void;
  /** Optional — when omitted the Hardware tile is not rendered. The project
   *  Add Line Item dialog omits it; the Cria Library passes it. */
  onSelectHardware?: () => void;
  /** Optional — when omitted the Metal Base tile is not rendered. */
  onSelectMetalBase?: () => void;
  /** Optional — when omitted the Die Wall tile is not rendered. The project
   *  Add Line Item dialog omits it; the Cria Library passes it. */
  onSelectDieWall?: () => void;
  /** Optional — when omitted the Decorative Pillow tile is not rendered. The
   *  project Add Line Item dialog omits it; the Cria Library passes it. This
   *  omission IS the library-only enforcement, backed by the two server-side
   *  guards in part-collector.ts and line-item-cost.service.ts. */
  onSelectPillow?: () => void;
  title?: string;
}

export default function AddLineItemDialog({
  isOpen,
  onClose,
  onSelectCustom,
  onSelectFromLibrary,
  onSelectBanquette,
  onSelectBackCushion,
  onSelectSeatCushion,
  onSelectArm,
  onSelectHardware,
  onSelectMetalBase,
  onSelectDieWall,
  onSelectPillow,
  title = 'Add Line Item',
}: AddLineItemDialogProps) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" classNames={{ base: 'bg-[var(--bg-surface)]', header: 'border-b border-[var(--border)]' }}>
      <ModalContent>
        <ModalHeader className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
          {title}
        </ModalHeader>
        <ModalBody className="py-6">
          <div className="grid grid-cols-1 gap-3">
            <button
              onClick={() => { onClose(); onSelectCustom(); }}
              className="flex items-center gap-4 p-4 rounded-lg border transition-colors hover:bg-[var(--bg-hover)]"
              style={{ borderColor: 'var(--border)' }}
            >
              <div className="p-2 rounded-lg" style={{ backgroundColor: '#2a3a4a' }}>
                <Package size={24} style={{ color: 'var(--accent)' }} />
              </div>
              <div className="text-left">
                <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Custom Item</div>
                <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Create a new item with custom dimensions and specs</div>
              </div>
            </button>

            <button
              onClick={() => { onClose(); onSelectFromLibrary(); }}
              className="flex items-center gap-4 p-4 rounded-lg border transition-colors hover:bg-[var(--bg-hover)]"
              style={{ borderColor: 'var(--border)' }}
            >
              <div className="p-2 rounded-lg" style={{ backgroundColor: '#1a3a2a' }}>
                <Layers size={24} style={{ color: '#2ecc71' }} />
              </div>
              <div className="text-left">
                <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>From Product Library</div>
                <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Choose a cabinet or frame template from the library</div>
              </div>
            </button>

            <button
              onClick={() => { onClose(); onSelectBanquette(); }}
              className="flex items-center gap-4 p-4 rounded-lg border transition-colors hover:bg-[var(--bg-hover)]"
              style={{ borderColor: 'var(--border)' }}
            >
              <div className="p-2 rounded-lg" style={{ backgroundColor: '#3a2a1a' }}>
                <Sofa size={24} style={{ color: '#e67e22' }} />
              </div>
              <div className="text-left">
                <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Banquette Base</div>
                <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Configure a banquette base with linear footage and profile</div>
              </div>
            </button>

            <button
              onClick={() => { onClose(); onSelectBackCushion(); }}
              className="flex items-center gap-4 p-4 rounded-lg border transition-colors hover:bg-[var(--bg-hover)]"
              style={{ borderColor: 'var(--border)' }}
            >
              <div className="p-2 rounded-lg" style={{ backgroundColor: '#2a1a3a' }}>
                <Armchair size={24} style={{ color: '#9b59b6' }} />
              </div>
              <div className="text-left">
                <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Back Cushion</div>
                <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Configure a banquette back cushion with upholstery design</div>
              </div>
            </button>

            <button
              onClick={() => { onClose(); onSelectSeatCushion(); }}
              className="flex items-center gap-4 p-4 rounded-lg border transition-colors hover:bg-[var(--bg-hover)]"
              style={{ borderColor: 'var(--border)' }}
            >
              <div className="p-2 rounded-lg" style={{ backgroundColor: '#1a2a3a' }}>
                <Square size={24} style={{ color: '#3498db' }} />
              </div>
              <div className="text-left">
                <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Seat Cushion</div>
                <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Configure a banquette seat cushion with upholstery design</div>
              </div>
            </button>

            <button
              onClick={() => { onClose(); onSelectArm(); }}
              className="flex items-center gap-4 p-4 rounded-lg border transition-colors hover:bg-[var(--bg-hover)]"
              style={{ borderColor: 'var(--border)' }}
            >
              <div className="p-2 rounded-lg" style={{ backgroundColor: '#3a1a2a' }}>
                <PanelLeft size={24} style={{ color: '#e74c3c' }} />
              </div>
              <div className="text-left">
                <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Arm</div>
                <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Configure a banquette arm with upholstery design running the depth</div>
              </div>
            </button>

            {onSelectHardware && (
              <button
                onClick={() => { onClose(); onSelectHardware(); }}
                className="flex items-center gap-4 p-4 rounded-lg border transition-colors hover:bg-[var(--bg-hover)]"
                style={{ borderColor: 'var(--border)' }}
              >
                <div className="p-2 rounded-lg" style={{ backgroundColor: '#2a2a3a' }}>
                  <Wrench size={24} style={{ color: '#95a5a6' }} />
                </div>
                <div className="text-left">
                  <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Hardware</div>
                  <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Link a Hardware Library item and price one installed unit</div>
                </div>
              </button>
            )}

            {onSelectMetalBase && (
              <button
                onClick={() => { onClose(); onSelectMetalBase(); }}
                className="flex items-center gap-4 p-4 rounded-lg border transition-colors hover:bg-[var(--bg-hover)]"
                style={{ borderColor: 'var(--border)' }}
              >
                <div className="p-2 rounded-lg" style={{ backgroundColor: '#1a3a3a' }}>
                  <RectangleHorizontal size={24} style={{ color: '#1abc9c' }} />
                </div>
                <div className="text-left">
                  <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Metal Base</div>
                  <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Price a stainless or painted metal base by the linear foot</div>
                </div>
              </button>
            )}

            {onSelectDieWall && (
              <button
                onClick={() => { onClose(); onSelectDieWall(); }}
                className="flex items-center gap-4 p-4 rounded-lg border transition-colors hover:bg-[var(--bg-hover)]"
                style={{ borderColor: 'var(--border)' }}
              >
                <div className="p-2 rounded-lg" style={{ backgroundColor: '#3a2a1a' }}>
                  <BrickWall size={24} style={{ color: '#e67e22' }} />
                </div>
                <div className="text-left">
                  <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Die Wall</div>
                  <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Price a Cria Layout die wall or fill by the cubic foot</div>
                </div>
              </button>
            )}

            {onSelectPillow && (
              <button
                onClick={() => { onClose(); onSelectPillow(); }}
                className="flex items-center gap-4 p-4 rounded-lg border transition-colors hover:bg-[var(--bg-hover)]"
                style={{ borderColor: 'var(--border)' }}
              >
                <div className="p-2 rounded-lg" style={{ backgroundColor: '#3a2a3a' }}>
                  <Bed size={24} style={{ color: '#b07bd4' }} />
                </div>
                <div className="text-left">
                  <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Decorative Pillow</div>
                  <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Price a throw pillow ex-fabric from its size, design and fill</div>
                </div>
              </button>
            )}
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
